import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';

const MultiSelect = ({ options, value, onChange, placeholder, label = 'option' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedValues = Array.isArray(value) ? value : [];
  const listRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackPlacements: ['bottom-end'],
        fallbackStrategy: 'bestFit',
      }),
      shift({ padding: 5 }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const handleToggleOption = (e, optionValue) => {
    e.stopPropagation();
    const newValue = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (!selectedValues.length) return placeholder || 'Select options...';
    return `${selectedValues.length} ${label}${selectedValues.length === 1 ? '' : 's'}`;
  };

  return (
    <div className="relative w-full">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="select select-bordered select-sm w-full cursor-pointer truncate"
      >
        {getDisplayText()}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[1001] bg-base-100 rounded-md shadow-lg border border-gray-200 overflow-hidden"
          >
            <ul className="py-1 max-h-60 overflow-y-auto" ref={listRef}>
              {options.map(option => (
                <li
                  key={option.value}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                  onClick={e => handleToggleOption(e, option.value)}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm flex-shrink-0"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => {}}
                  />
                  <span className="truncate">{option.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
};

export default MultiSelect;
